import React, { useState } from 'react';

/**
 * Accessible Tabs component using Tailwind CSS
 * - Keyboard accessible
 * - Controlled or uncontrolled
 * - Works with dynamic tab lists
 *
 * Usage:
 * <Tabs
 *   defaultValue="profile"
 *   tabs={[
 *     { label: 'Profile', value: 'profile', content: <div>Profile Content</div> },
 *     { label: 'Settings', value: 'settings', content: <div>Settings Content</div> },
 *     { label: 'Billing', value: 'billing', content: <div>Billing Content</div> },
 *   ]}
 * />
 */

export default function Tabs({
  tabs = [],
  value, // controlled
  defaultValue, // uncontrolled
  onChange,
  className = '',
}) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? tabs?.[0]?.value);

  const activeValue = isControlled ? value : internalValue;

  function setActive(val) {
    if (!isControlled) setInternalValue(val);
    onChange?.(val);
  }

  function handleKeyDown(e, index) {
    const total = tabs.length;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setActive(tabs[(index + 1) % total].value);
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActive(tabs[(index - 1 + total) % total].value);
    }
  }

  return (
    <div className={className}>
      {/* Tab List */}
      <div role="tablist" className="tw-flex tw-border-b tw-border-gray-200">
        {tabs.map((tab, index) => {
          const isActive = tab.value === activeValue;

          return (
            <button
              key={tab.value}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(tab.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`hover:tw-no-underline tw-font-semibold tw-px-[10px] tw-py-[6px] tw-border tw-border-solid tw-transition-colors
${
  isActive
    ? 'tw-bg-[#323333] tw-text-white tw-border-[#323333] hover:tw-bg-[#3f4040]'
    : 'tw-bg-white tw-text-[#323333] tw-border-[#323333]'
}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Panels */}
      <div className="mt-1">
        {tabs.map((tab) => (
          <div key={tab.value} role="tabpanel" hidden={tab.value !== activeValue} className="focus:tw-outline-none">
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------
// Example usage
// -----------------------------
// export function TabsDemo() {
//   const [tab, setTab] = useState('profile');
//
//   return (
//     <div className="max-w-md">
//       <Tabs
//         value={tab}
//         onChange={setTab}
//         tabs={[
//           { label: 'Profile', value: 'profile', content: <div>Profile Content</div> },
//           { label: 'Settings', value: 'settings', content: <div>Settings Content</div> },
//           { label: 'Billing', value: 'billing', content: <div>Billing Content</div> },
//         ]}
//       />
//
//       <div className="mt-4 text-sm text-gray-600">Active: {tab}</div>
//     </div>
//   );
// }
